import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import { GetPaymentRequestDetailsResponse, RequestControllerv2Service } from '@app/generated-api';
import { LabeledTextComponent } from '@app/v2/components/labeled-text/labeled-text.component';
import { RequestType } from '@app/v2/enums/request-types.enum';
import { CurrencyExchangeRateService } from '@app/v2/services/currency-exchange-rate/currency-exchange-rate.service';
import { CurrencySignalService } from '@app/v2/services/currency-signal/currency-signal.service';
import { resolveRequestStatusName } from '@app/v2/utils/request-status.utils';
import {
    computedAllDefined,
    disableFormEffect,
    effectAllDefinedNotNull,
    toSignalFromObservable,
} from '@app/v2/utils/signal-utils';
import {
    MdsButtonComponent,
    MdsDropdownComponent,
    MdsOption,
    MdsTextareaComponent,
    MdsTextFieldComponent,
} from '@medpacesoftwaredevelopment/designsystem';
import { PaymentRequestStatus } from '@models/request';
import { maxLengthValidator, notWhitespaceValidator } from '@utility/utility.validators';
import { debounceTime, map } from 'rxjs';
import { PaymentRequestStatusChangeFormGroup } from './payment-request-status-change-modal-content.form';

@Component({
    templateUrl: './payment-request-status-change-modal-content.component.html',
    selector: 'medpace-payment-request-status-change-modal-content',
    imports: [
        LabeledTextComponent,
        MdsDropdownComponent,
        MdsTextareaComponent,
        MdsButtonComponent,
        DecimalPipe,
        MdsTextFieldComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentRequestStatusChangeModalContentComponent {
    generatedRequestService = inject(RequestControllerv2Service);
    currencyService = inject(CurrencySignalService);
    currencyExchangeRateService = inject(CurrencyExchangeRateService);

    formGroup = input.required<PaymentRequestStatusChangeFormGroup>();
    requestType = input.required<RequestType>();
    requestId = input.required<number>();

    cancel = output<void>();
    confirm = output<PaymentRequestStatusChangeFormGroup>();

    modalContentInitialValue: () => ModalContent = () => ({
        newStatus: {
            notes: [],
        },
        totalAuthorized: null,
        deductedForm: null,
        confirmButton: {
            disabled: false,
            onClick: () => {
                if (this.formGroup().invalid) {
                    this.formGroup().markAllAsTouched();
                    return;
                }

                this.confirm.emit(this.formGroup());
            },
        },
        cancelButton: {
            onClick: () => {
                this.cancel.emit();
            },
        },
    });
    modalContent = signal<ModalContent>(this.modalContentInitialValue());

    formValue = {
        newStatusId: toSignalFromObservable(this.formGroup, (formGroup) =>
            formGroup.controls.newStatusId.valueChangesStartWithCurrentValue()
        ),
        deducted: toSignalFromObservable(this.formGroup, (formGroup) =>
            formGroup.controls.deductedFormGroup.controls.deducted
                .valueChangesStartWithCurrentValue()
                .pipe(debounceTime(300))
        ),
    };

    disableForm = disableFormEffect({
        form: () => this.formGroup().controls.deductedFormGroup,
        disabled: () => this.modalContent().deductedForm !== true,
    });

    paymentRequestDetails = rxResource({
        params: () => {
            const requestType = this.requestType();
            const requestId = this.requestId();
            const isPaymentRequest = requestType === RequestType.Reimbursement || requestType === RequestType.Stipend;

            if (!isPaymentRequest) return undefined;

            return { requestType, requestId };
        },
        stream: ({ params }) =>
            this.generatedRequestService.requestControllerv2GetPaymentRequestDetails(
                params.requestType,
                params.requestId
            ),
    });
    patientsCurrency = this.currencyService.getCurrencyByCodeOrName(
        computed(() => this.paymentRequestDetails.value()?.patient.preferredCurrency)
    );
    totalCurrency = this.currencyService.getCurrencyByCodeOrName(
        computed(() => this.paymentRequestDetails.value()?.totalCurrency)
    );
    deductedCurrency = computed(() =>
        this.postCurrencyConversionMigration() ? this.patientsCurrency() : this.totalCurrency()
    );
    isDeductedCurrencyUsd = computed(() => this.deductedCurrency()?.isocode.toUpperCase() === 'USD');

    deductedUsd = rxResource({
        params: () => {
            const deducted = this.formValue.deducted();
            const currency = this.deductedCurrency();
            const paymentRequestDetails = this.paymentRequestDetails.value();
            const amount = Number(deducted);
            if (deducted === null || deducted === undefined || `${deducted}` === '' || isNaN(amount)) return undefined;
            if (
                !currency ||
                this.isDeductedCurrencyUsd() ||
                !paymentRequestDetails ||
                !paymentRequestDetails.exchangeRateDate
            )
                return undefined;

            return {
                amount,
                currencyCode: currency.isocode.toUpperCase(),
                exchangeRateDate: paymentRequestDetails.exchangeRateDate,
            };
        },
        stream: ({ params }) =>
            this.currencyExchangeRateService
                .convertAmount(params.amount, params.currencyCode, params.exchangeRateDate, null)
                .pipe(map((res) => res.usd)),
    });

    syncDeductedUsd_Effect = effect(() => {
        const deductedUsd = this.isDeductedCurrencyUsd()
            ? (this.formValue.deducted() ?? null)
            : (this.deductedUsd.value() ?? null);
        this.formGroup().controls.deductedFormGroup.controls.deductedUsd.setValue(deductedUsd);
    });

    syncDeductedCurrency_Effect = effect(() => {
        const deductedCurrency = this.deductedCurrency() ?? null;
        this.formGroup().controls.deductedFormGroup.controls.deductedCurrency.setValue(deductedCurrency);
    });

    paymentRequestDetails_CurrentStatusId = computedAllDefined(
        { paymentRequestDetails: this.paymentRequestDetails.value },
        ({ paymentRequestDetails }) => paymentRequestDetails.statusId as PaymentRequestStatus
    );
    currentStatus = computedAllDefined(
        { currentStatusId: this.paymentRequestDetails_CurrentStatusId },
        ({ currentStatusId }) => resolveRequestStatusName(this.requestType(), currentStatusId, false)
    );

    options = computedAllDefined(
        {
            paymentRequestDetails: this.paymentRequestDetails.value,
        },
        ({ paymentRequestDetails }) => {
            return paymentRequestDetails.availableStatuses.map((status) => {
                return <MdsOption<PaymentRequestStatus>>{
                    value: status.id as PaymentRequestStatus,
                    viewValue: resolveRequestStatusName(this.requestType(), status.id, false),
                };
            });
        }
    );

    updateForm_Effect = effectAllDefinedNotNull(
        {
            newStatusId: this.formValue.newStatusId,
        },
        ({ newStatusId }) => {
            if (newStatusId === PaymentRequestStatus.Denied) {
                this.setValidators.withRequired();
            } else if (newStatusId === PaymentRequestStatus.PayoutIssue) {
                this.setValidators.withRequired();
            } else {
                this.setValidators.default();
            }
            this.formGroup().controls.additionalInfo.updateValueAndValidity();
        }
    );

    setValidators = {
        withRequired: () => {
            this.formGroup().controls.additionalInfo.setValidators([
                maxLengthValidator(500),
                Validators.required,
                notWhitespaceValidator(),
            ]);
        },
        default: () => {
            this.formGroup().controls.additionalInfo.setValidators([maxLengthValidator(500)]);
        },
    };
    postCurrencyConversionMigration = computed(() => {
        const paymentRequestDetails = this.paymentRequestDetails.value();
        if (!paymentRequestDetails) return undefined;

        return (
            paymentRequestDetails.totalAuthorizedAmountPatientsCurrency != null &&
            paymentRequestDetails.totalAuthorizedAmountPatientsCurrency != undefined &&
            paymentRequestDetails.totalAuthorizedAmountUsd != null &&
            paymentRequestDetails.totalAuthorizedAmountUsd != undefined
        );
    });

    setUI_Effect = effectAllDefinedNotNull(
        {
            paymentRequestDetails: this.paymentRequestDetails.value,
            newStatusId: this.formValue.newStatusId,
        },
        ({ newStatusId, paymentRequestDetails }) => {
            let modalContent = this.modalContentInitialValue();

            if (newStatusId === PaymentRequestStatus.PendingPSApproval) {
                modalContent = this.setUI.fromAny_ToPendingPSApproval(modalContent, paymentRequestDetails);
            } else if (newStatusId === PaymentRequestStatus.PendingERSubmission) {
                modalContent = this.setUI.fromAny_ToPendingERSubmission(modalContent, paymentRequestDetails);
            } else if (newStatusId === PaymentRequestStatus.Voided) {
                modalContent = this.setUI.fromAny_ToVoided(modalContent);
            }

            this.modalContent.set(modalContent);
        }
    );

    setUI = {
        fromAny_ToPendingPSApproval: (
            modalContent: ModalContent,
            paymentRequestDetails: GetPaymentRequestDetailsResponse
        ) => {
            modalContent.totalAuthorized = {
                inPatientsCurrency: paymentRequestDetails.patient.preferredCurrency !== 'USD' ? true : false,
                inUSD: true,
            };
            const siteSupportsPQ = paymentRequestDetails.patient.site.paymentPayQuicker ?? false;
            const patientIsReadyForPayment = paymentRequestDetails.patient.readyForPayment ?? false;
            if (siteSupportsPQ) {
                if (!patientIsReadyForPayment) {
                    modalContent.newStatus.notes = [
                        'Request cannot be set as Pending PS Approval because the patient is not ready for payment. Make sure proper forms have been uploaded to Patient Profile.',
                    ];
                    modalContent.confirmButton.disabled = true;
                }
            }

            return modalContent;
        },
        fromAny_ToPendingERSubmission: (
            modalContent: ModalContent,
            paymentRequestDetails: GetPaymentRequestDetailsResponse
        ) => {
            const siteRequiresPaymentConfirmation =
                paymentRequestDetails.patient.site.paymentConfirmationRequired ?? false;
            const patientIsReadyForPayment = paymentRequestDetails.patient.readyForPayment ?? false;

            modalContent.totalAuthorized = {
                inPatientsCurrency: paymentRequestDetails.patient.preferredCurrency !== 'USD' ? true : false,
                inUSD: true,
            };

            if (siteRequiresPaymentConfirmation && !patientIsReadyForPayment) {
                modalContent.newStatus.notes = [
                    'Request cannot be completed because the patient is not ready for payment. Make sure proper forms have been uploaded to Patient Profile.',
                ];
                modalContent.confirmButton.disabled = true;
            }

            return modalContent;
        },
        fromAny_ToVoided: (modalContent: ModalContent) => {
            modalContent.deductedForm = true;
            modalContent.totalAuthorized = null;

            return modalContent;
        },
    };
}
export type ModalContent = {
    newStatus: {
        notes: string[];
    };
    totalAuthorized: {
        inPatientsCurrency: boolean | null;
        inUSD: boolean | null;
    } | null;
    deductedForm: boolean | null;
    confirmButton: { disabled: boolean; onClick: () => void };
    cancelButton: { onClick: () => void };
};
